import { _, helpers, hooks, mui, React, ts, ui, useSelector } from '_core';

import { prepareBarData, prepareData } from './helpers';
import { DraftParams, Params } from './types';
import { nestedBarChartFormat } from '../../common/formatters/nested-bar-chart';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  file: string;
  fullScreen: boolean;
  params: Params | DraftParams;
  setParams: (_p: DraftParams) => void;
  title: string;
};

const Chart = ({ id, readOnly, loadWidgetData, file, fullScreen, title, params, setParams }: ChartProps) => {
  const uiStyles = hooks.useUiStyles();

  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);

  const [plotType, setPlotType] = React.useState(params.plot_type || 'line');

  const resources = useSelector((state) => state.resources);

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file,
          rename: { build_date: 'date' },
        },
      });

      if (response) setData(response.data);
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (file) loadData();
    return () => setData(null);
  }, [file]);

  const formattedData = React.useMemo(() => {
    if (_.isNil(data)) return null;

    if (plotType === 'bar') {
      const nestedBarData = nestedBarChartFormat(data, 'date');
      return prepareBarData(resources, nestedBarData.data);
    }

    return prepareData(resources, data);
  }, [data, plotType]);

  const debouncedSaveParams = React.useMemo(
    () =>
      _.debounce((plotType) => {
        setParams({
          ...params,
          plot_type: plotType,
        });
      }, 1000),
    []
  );

  hooks.useEffectWithoutFirst(() => {
    debouncedSaveParams(plotType);
  }, [plotType]);

  return (
    <mui.core.Box display="flex" flexDirection="column" style={{ height: '100%' }}>
      <mui.core.Box px={4} mt={2} sx={uiStyles.hidePrint}>
        <mui.core.FormControl component="fieldset">
          <mui.core.RadioGroup
            row
            aria-label="position"
            name="position"
            value={plotType}
            onChange={(e) => {
              setPlotType(e.target.value as 'line' | 'bar');
            }}
          >
            <mui.core.FormControlLabel
              value="line"
              control={<mui.core.Radio color="primary" />}
              label="Plot as Line Chart"
            />
            <mui.core.FormControlLabel
              value="bar"
              control={<mui.core.Radio color="primary" />}
              label="Plot as Bar Chart"
            />
          </mui.core.RadioGroup>
        </mui.core.FormControl>
      </mui.core.Box>

      <mui.core.Box p={3} flex="1">
        {plotType === 'line' ? (
          <ui.LineChart
            id={id}
            data={formattedData}
            fullScreen={fullScreen}
            readOnly={readOnly}
            alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
            exportTitle={title}
          />
        ) : (
          <ui.NestedBarChart
            id={id}
            data={formattedData}
            category="category"
            rotation={90}
            alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
            fullScreen={fullScreen}
            exportTitle={title}
            readOnly={readOnly}
          />
        )}
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
